// Assume algoliasearch is available globally from a CDN script include.
// If not, it needs to be loaded via a script tag in the HTML.

export class AlgoliaSearchManager {
  constructor(
    appId,
    searchKey,
    indexName,
    inputElementId,
    resultsElementId,
    paginationElementId,
    resultsCountElementId,
    clearFilterElementId,
    initialFilters = null,
    initialSortByIndex = null,
    initialQuery = "", // New parameter for initial query
    onSearchTriggered = () => {}, // New callback for search trigger
  ) {
    this.searchClient = algoliasearch(appId, searchKey);
    this.currentAlgoliaIndexName = initialSortByIndex || indexName;
    this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName);

    this.initialQuery = initialQuery; // Store initial query

    this.onSearchTriggered = onSearchTriggered; // Store the callback

    this.searchInput = document.getElementById(inputElementId);
    this.searchResults = document.getElementById(resultsElementId);
    this.paginationContainer = document.getElementById(paginationElementId);
    this.resultsCountElement = document.getElementById(resultsCountElementId);
    this.clearFilterButton = document.getElementById(clearFilterElementId);

    if (this.clearFilterButton) {
      this.clearFilterButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.clear();
        this.onSearchTriggered(""); // Notify that search is cleared
        this.updateUrl(""); // Clear search param from URL
      });
    }

    this.searchTimeout = null;
    this.currentPage = 0;
    this.totalPages = 0;
    this.currentQuery = initialQuery; // Initialize currentQuery with initialQuery
    this.algoliaFilters = null;
    this.currentFacetFilters = {}; // To store multiple facet filters

    // Apply escaping to initial filters
    this.algoliaFilters = initialFilters;

    this.initEventListeners();

    if (this.searchInput && this.initialQuery) {
      this.searchInput.value = this.initialQuery;
    }

    // Perform initial search to show filtered results by default
    if (this.initialQuery) {
      this.performSearch(this.initialQuery);
      this.showSearchResults();
    } else {
      this.hideSearchResults(); // Hide results if no initial query
    }
  }

  initEventListeners() {
    if (this.searchInput) {
      this.searchInput.addEventListener("input", (e) => {
        const query = e.target.value.trim();
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.performSearch(query);
          this.showSearchResults();
          this.onSearchTriggered(query); // Call the new callback
          this.updateUrl(query); // Update URL on search
        }, 300);
      });

      // Prevent Enter key from submitting/reloading the page
      this.searchInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      });
    }
  }

  updateUrl(query) {
    const url = new URL(window.location.href);
    if (query) {
      url.searchParams.set("search", query);
    } else {
      url.searchParams.delete("search");
    }
    window.history.pushState({}, "", url);
  }

  performSearch(query, page = 0) {
    this.currentQuery = query;
    this.currentPage = page;

    if (!query.trim()) {
      this.hideSearchResults();
      return;
    }

    // Re-initialize index if the sort index has changed
    if (this.index.indexName !== this.currentAlgoliaIndexName) {
      this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName);
    }

    const searchOptions = {
      hitsPerPage: 20,
      page: page,
      attributesToSnippet: ["content:50"], // Increased snippet length to 50 words
      distinct: true, // Add this line to enable distinct results
    };

    let allFilters = [];

    // Exclude records where show_on_site_search is false
    allFilters.push("NOT show_on_site_search:false");

    if (this.algoliaFilters) {
      allFilters.push(this.algoliaFilters);
    }

    for (const attribute in this.currentFacetFilters) {
      if (this.currentFacetFilters[attribute]) {
        allFilters.push(this.currentFacetFilters[attribute]);
      }
    }

    if (allFilters.length > 0) {
      searchOptions.filters = allFilters.join(" AND ");
    }

    this.index
      .search(query, searchOptions)
      .then(({ hits, nbHits, nbPages, page }) => {
        this.totalPages = nbPages;
        this.currentPage = page;
        this.renderResults(hits);
        this.renderPagination(nbPages, page);
        this.updateResultsCount(nbHits, hits.length);
        this.showSearchResults(); // Ensure results are shown after a successful search
      })
      .catch((err) => {
        console.error("Algolia search error:", err);
        // Simplified error handling: just render empty results on error
        this.renderResults([]);
        this.renderPagination(0, 0);
        this.updateResultsCount(0, query);
        this.hideSearchResults(); // Hide results on error
      });
  }

  renderResults(hits) {
    if (!this.searchResults) return;

    if (hits.length === 0) {
      this.searchResults.innerHTML = `
        <div class="py-8 text-center text-gray">No results found matching your search</div>
      `;
      return;
    }

    const resultsHtml = hits
      .map((hit) => {
        const externalIcon = hit.is_external_link
          ? '<span class="whitespace-nowrap decoration-none">&nbsp;<svg class="inline-block" width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.18022 9.96831L9.2283 4.89553L3.81893 10.3049L3.07364 9.55961L8.48301 4.15024L3.3982 4.1863L4.38391 3.20059L10.1779 3.20059L10.1659 8.98261L9.18022 9.96831Z" fill="#333333"/></svg></span>'
          : "";
        const targetAttr = hit.is_external_link ? ' target="_blank"' : "";

        return `
          <li class="box-border flex flex-row items-start py-8 gap-8 w-full border-b border-lightborder">
            <div class="flex flex-col items-start p-0 gap-3 flex-grow">
              <a href="${hit.url}" class="text-serif-md subtle-link font-bold text-gray self-stretch"${targetAttr}>
                ${hit.title}${externalIcon}
              </a>
              <div class="relative w-full">
            <p class="text-sans-md text-gray [&_em]:not-italic">
              ${hit._highlightResult?.post_full_content?.value || hit.post_full_content || hit._snippetResult?.content?.value || hit.description || ""}
                </p>
              </div>
              ${
                hit.post_type
                  ? `<p class="text-sans-sm text-black self-stretch">
                      ${hit.post_type}${hit.publication_date && hit.post_type === "Research" ? ` <span class="text-gray-500 ml-1 mr-1">/</span> ${new Date(hit.publication_date * 1000).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}` : ""}
                    </p>`
                  : ""
              }
            </div>
            ${hit.featured_image ? `<img src="${hit.featured_image}" alt="${hit.title}" class="w-[75px] h-[75px] object-cover flex-none">` : ""}
          </li>
        `;
      })
      .join("");

    this.searchResults.innerHTML = `
      <div class="flex flex-col mt-16">
        <div id="algolia-results-count" class="box-border flex flex-row items-center py-0 px-0 pb-3 gap-[10px] w-full h-[19px] border-b border-[#D7D7D7] mb-[10px]">
          <span class="text-sans-xs font-normal text-gray"></span>
        </div>
        <ul>
          ${resultsHtml}
        </ul>
      </div>
    `;
  }

  renderPagination(totalPages, currentPage) {
    if (!this.paginationContainer) return;

    if (totalPages <= 1) {
      this.paginationContainer.style.display = "none";
      return;
    }

    this.paginationContainer.style.display = "block";

    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    let pagesHtml = "";
    for (let i = startPage; i <= endPage; i++) {
      const activeClass = i === currentPage ? "before:scale-y-100" : "";
      pagesHtml += `
        <li>
          <a class="text-sans-sm w-9 h-12 flex relative before:content-[''] before:absolute before:w-full before:h-[3px] before:top-0 before:left-0 before:scale-y-0 before:origin-top before:transition-all hover:before:scale-y-100 before:bg-black ${activeClass}"
             href="#" data-page="${i}">
            <span class="m-auto">${i + 1}</span>
          </a>
        </li>
      `;
    }

    const prevDisabled =
      currentPage === 0 ? "opacity-50 pointer-events-none" : "";
    const nextDisabled =
      currentPage === totalPages - 1 ? "opacity-50 pointer-events-none" : "";

    this.paginationContainer.innerHTML = `
      <div class="flex flex-col md:flex-row justify-between items-center md:items-start">
        <ul class="flex flex-row flex-nowrap">
          ${pagesHtml}
        </ul>
        <ul class="flex flex-nowrap">
          <li>
            <a class="inline-block relative w-9 h-12 before:content-[url(../assets/chevron.svg)] before:absolute before:top-[50%] before:left-[50%] before:translate-[-50%] before:origin-center before:rotate-90 border-r-1 border-lightborder ${prevDisabled}"
               href="#" data-page="${currentPage - 1}" id="algolia-prev">
              <span class="sr-only">Previous</span>
            </a>
          </li>
          <li>
            <a class="inline-block relative w-9 h-12 before:content-[url(../assets/chevron.svg)] before:absolute before:top-[50%] before:left-[50%] before:translate-[-50%] before:origin-center before:-rotate-90 ${nextDisabled}"
               href="#" data-page="${currentPage + 1}" id="algolia-next">
              <span class="sr-only">Next</span>
            </a>
          </li>
        </ul>
      </div>
    `;

    this.paginationContainer
      .querySelectorAll("a[data-page]")
      .forEach((link) => {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          const page = parseInt(link.dataset.page);
          if (page >= 0 && page < totalPages && page !== currentPage) {
            this.performSearch(this.currentQuery, page);
          }
        });
      });
  }

  updateResultsCount(total = 0, hitsLength = 0) {
    const resultsCountSpan = this.searchResults?.querySelector(
      "#algolia-results-count span",
    );
    if (resultsCountSpan) {
      resultsCountSpan.textContent = `Showing ${hitsLength} of ${total} results`;
    }
  }

  showSearchResults() {
    if (this.searchResults) this.searchResults.style.display = "block";
    if (this.paginationContainer && this.totalPages > 1) {
      this.paginationContainer.style.display = "block";
    }
  }

  hideSearchResults() {
    if (this.searchResults) this.searchResults.style.display = "none";
    if (this.paginationContainer)
      this.paginationContainer.style.display = "none";
  }

  escapeAlgoliaFilterValue(value) {
    if (typeof value !== "string") {
      return value;
    }
    const doc = new DOMParser().parseFromString(value, "text/html");
    const decodedValue = doc.documentElement.textContent;
    const needsQuotes = /[&()![\]|<>=:*_~^?/\\]|\s/.test(decodedValue);
    return needsQuotes ? `"${decodedValue}"` : decodedValue;
  }

  setAlgoliaFilters(filters) {
    if (filters && typeof filters === "string" && filters.includes(":")) {
      const [attribute, value] = filters.split(":", 2);
      this.algoliaFilters = `${attribute}:${this.escapeAlgoliaFilterValue(value)}`;
    } else {
      this.algoliaFilters = filters;
    }
  }

  setAlgoliaFilter(attribute, value) {
    if (value) {
      this.currentFacetFilters[attribute] =
        `${attribute}:${this.escapeAlgoliaFilterValue(value)}`;
    } else {
      delete this.currentFacetFilters[attribute];
    }
  }

  getAlgoliaFacets(attribute) {
    const searchOptions = {
      facets: [attribute],
      hitsPerPage: 0,
    };
    if (this.algoliaFilters) {
      searchOptions.filters = this.algoliaFilters;
    }
    return this.index
      .search("", searchOptions)
      .then(({ facets }) => {
        return facets[attribute] || {};
      })
      .catch((err) => {
        console.error(`Error fetching facets for ${attribute}:`, err);
        return {};
      });
  }

  setAlgoliaSortByIndex(indexName) {
    this.currentAlgoliaIndexName = indexName;
    this.index = this.searchClient.initIndex(this.currentAlgoliaIndexName); // Re-initialize index immediately
  }

  getAlgoliaCurrentQuery() {
    return this.currentQuery;
  }

  setAlgoliaFocusAreaFilter(focusArea) {
    this.setAlgoliaFilter("focus-area", focusArea);
  }

  clear(shouldPerformSearch = true) {
    this.currentQuery = "";
    this.currentPage = 0;
    this.currentPriceRangeFilter = null;
    this.currentFacetFilters = {};
    if (this.searchInput) {
      this.searchInput.value = "";
    }
    if (shouldPerformSearch) {
      this.performSearch("");
    }
  }
}

let algoliaManagerInstance = null;

export function initAlgoliaSearch(...args) {
  if (!algoliaManagerInstance) {
    algoliaManagerInstance = new AlgoliaSearchManager(...args);
  }
}

export function performSearch(...args) {
  algoliaManagerInstance?.performSearch(...args);
}

export function setAlgoliaFilters(...args) {
  algoliaManagerInstance?.setAlgoliaFilters(...args);
}

export function setAlgoliaSortByIndex(...args) {
  algoliaManagerInstance?.setAlgoliaSortByIndex(...args);
}

export function getAlgoliaCurrentQuery(...args) {
  return algoliaManagerInstance?.getAlgoliaCurrentQuery(...args);
}

export function getAlgoliaFacets(...args) {
  return algoliaManagerInstance?.getAlgoliaFacets(...args);
}

export function setAlgoliaFocusAreaFilter(...args) {
  algoliaManagerInstance?.setAlgoliaFocusAreaFilter(...args);
}
